import { api, type NewsItem } from '../../lib/api';
import { useAsync } from '../../lib/dataHooks';
import { useSelection } from '../../lib/selection';
import { isoWeekKey, isoWeekMonday, isoWeekSunday, monthKey, yearKey } from '../../lib/scopeKey';
import { useState } from 'react';

/**
 * 新闻面板 —— 接入 /api/news（需求 §F2.9 / §4.4）
 *
 * 三条不可违背的规则：
 *  1. 新闻**全局共享**：不按指数过滤，所有指数看到同一条时间线
 *  2. `date` 是**原始发布自然日**，永不对齐交易日 —— 故与图表日期独立
 *  3. 休市日的新闻归入**下一个交易日分组**显示，但 date 字段不改（仅 UI 分组）
 */
export function NewsPanel() {
  const { instrument, period, effectiveDate } = useSelection();
  /**
   * 日期不再由本面板自己维护（改前是 `todayISO()` 的独立状态），
   * 改为消费统一的 selection.date —— 笔记与新闻共用一个日期源。
   *
   * scope 随周期：日 scope 取该交易日（服务端自动纳入归属到它的休市日新闻）；
   * 周/月/年 scope 取整个区间（本周的休市日新闻也在其中）。
   */
  const isDayScope = period === 'D' || period === 'MIN1' || period === 'MIN5';
  const range = effectiveDate ? scopeRange(period, effectiveDate) : null;
  const [mode, setMode] = useState<'scope' | 'starred' | 'search'>('scope');
  const [q, setQ] = useState('');
  const [starBusy, setStarBusy] = useState<string | null>(null);

  /**
   * scope 列表每 60 秒自动重取。
   *
   * 服务器侧已改为新闻独立定时抓取（不再被归档的盘时段门控停掉，
   * 见 server-go/internal/job/news.go），客户端只需把库里的新条目
   * 及时显示出来。keepPrevious 保证每轮刷新列表不闪、不跳回顶部。
   *
   * 只轮询 scope：starQ（自己的收藏）与 searchQ（用户输入驱动）
   * 不随时间变化，轮询它们纯属浪费请求。
   */
  const scopeQ = useAsync(() => {
    if (!effectiveDate || !range) return Promise.resolve(null);
    // 日 scope 走 K 线联动入口：code + tradeDate → 服务端做休市归属
    if (isDayScope) {
      return api.news({ code: instrument.code, tradeDate: effectiveDate, limit: 200 });
    }
    return api.news({ from: range.from, to: range.to, limit: 200 });
  }, [instrument.code, period, effectiveDate, isDayScope],
    { enabled: mode === 'scope' && !!effectiveDate, keepPrevious: true, pollMs: 60_000 });

  const starQ = useAsync(() => api.news({ starred: true, limit: 200 }),
    [], { enabled: mode === 'starred' });

  const searchQ = useAsync(() => api.news({ q, limit: 200 }),
    [q], { enabled: mode === 'search' && q.trim().length > 0 });

  const active = mode === 'scope' ? scopeQ : mode === 'starred' ? starQ : searchQ;
  const items = active.data?.items ?? [];

  /** 休市归属：把休市日的新闻合并到下一个已有新闻的日期分组下 */
  const groups = groupByDate(items);

  /**
   * 归属提示：如实告知休市日的新闻去哪了，否则用户会以为漏了消息。
   *
   * ⚠️ 必须**分方向**措辞，两者的语义完全不同：
   *  · 后向（naturalDates < tradeDate）：休市日新闻按 F2.9 归到下一个交易日，
   *    确实「已归入」tradeDate —— 国庆看 10-08 就是这种。
   *  · 前向（naturalDates > tradeDate）：tradeDate 是日历里最后一个交易日，
   *    它后面的休市日新闻**还没等到自己的下一个交易日**，只是暂时并入本视图，
   *    说「已归入」是错的 —— 周日看 10-09 属于这种。
   */
  const attributionNote = (() => {
    if (mode !== 'scope' || !active.data?.attributed) return null;
    const td = active.data.tradeDate;
    if (!td) return null;
    const ds = active.data.naturalDates ?? [];
    const label = (xs: string[]) => (xs.length > 3
      ? `${xs[0]!.slice(5)}~${xs[xs.length - 1]!.slice(5)}`
      : xs.map((d) => d.slice(5)).join('、'));

    const before = ds.filter((d) => d < td);
    const after = ds.filter((d) => d > td);
    const parts: string[] = [];
    if (after.length > 0) {
      parts.push(`${label(after)} 休市，新闻一并显示（下一个交易日尚未产生）`);
    }
    if (before.length > 0) {
      parts.push(`${label(before)} 休市，新闻已归入 ${td.slice(5)}`);
    }
    return parts.length > 0 ? parts.join('；') : null;
  })();

  const toggleStar = async (it: NewsItem) => {
    setStarBusy(it.id);
    try {
      await api.setStarred(it.id, !it.starred);
      scopeQ.reload();
      starQ.reload();
      searchQ.reload();
    } catch {
      /* 失败时保持原状态，下一次点击会重试 */
    } finally {
      setStarBusy(null);
    }
  };

  const starredCount = items.filter((i) => i.starred).length;

  return (
    <section className="flex min-h-0 flex-1 flex-col">
      <div className="flex h-8 shrink-0 items-center gap-2 border-b px-3 text-[12px]"
        style={{ background: 'var(--bg-surface)' }}>
        <span className="font-medium">📰 新闻</span>
        <span className="num" style={{ color: 'var(--text-3)' }}>
          {active.status === 'loading' ? '…' : `${items.length} 条`}
          {starredCount > 0 && ` · ★${starredCount}`}
        </span>

        <div className="ml-auto flex items-center gap-1">
          {/*
            日期选择已上移到统一筛选器（DateScopeFilter），
            此处只回显当前筛选的区间，避免两处日期入口互相覆盖。
          */}
          <span className="num text-[11px]" style={{ color: 'var(--text-2)' }}
            title="由上方日期筛选器决定">
            {range ? (range.from === range.to ? range.from : `${range.from}~${range.to}`) : '—'}
          </span>
          {/* 手动刷新：只是重取列表（GET），不限权限。
              真正向新闻源抓取的是管理员的 POST /api/news/sync，
              自动轮询时无需人工介入，这里只是给「等不及下一轮」的人一个出口 */}
          <button type="button" onClick={active.reload}
            disabled={active.status === 'loading'}
            className="shrink-0 rounded px-1 text-[11px] disabled:opacity-40"
            style={{ color: 'var(--text-3)' }}
            aria-label="刷新新闻"
            title="重新读取列表">
            ↻
          </button>
          <button
            type="button"
            /*
             * 开关：收藏态下再点一次回普通列表。
             *
             * ⚠️ 原来只有 `setMode('starred')`，永远退不出去 ——
             *    唯一的绕法是在搜索框输点东西再点「清除」，或者切走 Tab
             *    再回来靠重新挂载复位。而它带 aria-pressed，语义上就是开关，
             *    按下却抬不起来。
             *
             * 两个分支都清空 q：否则从搜索态切进来、再切回去，搜索框还留着
             * 上次的词，可列表已经不是搜索结果，看着像在搜却没搜。
             */
            onClick={() => { setQ(''); setMode(mode === 'starred' ? 'scope' : 'starred'); }}
            className="rounded px-1.5 py-0.5 text-[11px]"
            style={{ background: mode === 'starred' ? 'var(--bg-elevated)' : 'transparent',
              color: mode === 'starred' ? 'var(--star)' : 'var(--text-3)' }}
            title={mode === 'starred' ? '取消收藏筛选' : '只看收藏'}
            aria-label={mode === 'starred' ? '取消收藏筛选' : '只看收藏'}
            aria-pressed={mode === 'starred'}>★</button>
        </div>
      </div>

      {/* 休市归属提示：如实告知，否则用户以为漏了消息 */}
      {attributionNote && (
        <div className="shrink-0 px-3 py-1 text-[11px]"
          style={{ background: 'var(--bg-elevated)', color: 'var(--text-3)' }}>
          ⓘ {attributionNote}
        </div>
      )}

      {/* 搜索框 */}
      <div className="flex shrink-0 items-center gap-1.5 border-b px-2 py-1">
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); setMode('search'); }}
          onFocus={() => setMode('search')}
          placeholder="搜索新闻标题…"
          className="min-w-0 flex-1 rounded px-2 py-1 text-[12px]"
          style={{ background: 'var(--bg-inset)', color: 'var(--text-1)' }}
        />
        {mode === 'search' && (
          <button type="button" onClick={() => { setQ(''); setMode('scope'); }}
            className="shrink-0 text-[11px]" style={{ color: 'var(--text-3)' }}>清除</button>
        )}
      </div>

      {active.status === 'error' && (
        <div className="shrink-0 px-3 py-2 text-[12px]" style={{ color: 'var(--warn)' }}>
          ⚠ {active.error}
          <button type="button" onClick={active.reload}
            className="ml-2 underline">重试</button>
        </div>
      )}

      {mode === 'search' && q.trim() === '' && (
        <div className="px-3 py-2 text-[12px]" style={{ color: 'var(--text-3)' }}>
          输入关键词搜索全部历史新闻
        </div>
      )}

      {mode === 'search' && q.trim() !== '' && items.length === 0 && active.status === 'ready' && (
        <div className="px-3 py-2 text-[12px]" style={{ color: 'var(--text-3)' }}>无匹配结果</div>
      )}

      {mode === 'starred' && items.length === 0 && active.status === 'ready' && (
        <div className="px-3 py-2 text-[12px]" style={{ color: 'var(--text-3)' }}>尚无收藏</div>
      )}

      {/* 铺满 tab 体并内部滚动（WorkZone 固定 60vh）：
          原为 max-h-[60vh] 随条数在 44~360px 之间伸缩，正是切换时页面跳动的来源 */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {groups.map((g) => (
          <div key={g.date}>
            <div className="sticky top-0 z-10 flex items-center gap-2 border-b px-3 py-1.5 text-[11px]"
              style={{ color: 'var(--text-2)', background: 'var(--bg-surface)' }}>
              <span>▾ {g.date.slice(5)}</span>
              <span className="num" style={{ color: 'var(--text-3)' }}>
                {g.items.length} 条
              </span>
            </div>
            {g.items.map((it) => (
              <div key={it.id} className="group flex gap-2 border-b px-3 py-2 last:border-b-0">
                <span className="num shrink-0 text-[11px]" style={{ color: 'var(--text-3)' }}>
                  {/*
                    published_at 全库统一为 "YYYY-MM-DD HH:MM:SS"
                    （上游东财 showTime 原样透传，1197 条无 ISO / T / 时区混入），
                    故直接切片取 "MM-DD HH:MM"。上游哪天改格式，这里会静默截出
                    乱码，改动前请先核对格式。

                    必须带日期：合并组（▾ 10-11 · 含 10-10）与搜索、收藏视图
                    都跨天，只显示 HH:MM 分不清是哪天的。
                  */}
                  {it.publishedAt.slice(5, 16)}
                </span>
                <a
                  href={it.url ?? '#'}
                  target={it.url ? '_blank' : undefined}
                  rel="noreferrer"
                  className="line-clamp-2 flex-1 text-[13px] leading-[1.45] no-underline hover:underline"
                  style={{ color: 'var(--text-1)' }}
                  title={it.summary ?? it.title}
                >
                  {it.title}
                  {it.media && (
                    <span className="ml-1.5 text-[10px]" style={{ color: 'var(--text-3)' }}>
                      {it.media}
                    </span>
                  )}
                </a>
                <button
                  type="button"
                  onClick={() => void toggleStar(it)}
                  disabled={starBusy === it.id}
                  aria-label={it.starred ? '取消收藏' : '收藏'}
                  aria-pressed={it.starred}
                  className="shrink-0 text-[11px] disabled:opacity-50"
                  style={{ color: it.starred ? 'var(--star)' : 'var(--text-3)' }}
                >
                  {it.starred ? '★' : '☆'}
                </button>
              </div>
            ))}
          </div>
        ))}

        {active.status === 'loading' && (
          <div className="px-3 py-3 text-[12px]" style={{ color: 'var(--text-3)' }}>加载中…</div>
        )}

        {effectiveDate && mode === 'scope' && items.length === 0 && active.status === 'ready' && (
          <div className="px-3 py-3 text-[12px]" style={{ color: 'var(--text-3)' }}>
            {range ? `${range.from}${range.from !== range.to ? ` ~ ${range.to}` : ''} 无新闻记录。` : '无新闻记录。'}
            可用上方筛选器换日期，或点同步按钮补抓。
            {range && (
              <button
                type="button"
                onClick={async () => {
                  await api.syncNews({ from: range.from, to: range.to });
                  scopeQ.reload();
                }}
                className="ml-2 underline"
              >
                同步该区间
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

interface Group { date: string; items: NewsItem[] }

/**
 * 按日期分组，**每天一个分组头**，按日期降序。
 *
 * ⚠️ 原实现把「周六周日」并入上一组，结果任意且看错（实测真实数据）：
 *
 *	看 10-09 → ["10-11(153 +10-10)", "10-09(101)"]，10-10 没有头
 *	看 10-08 → 10-08/07/06 各有头，10-05 却吞掉 10-04、10-03，
 *	            10-02、10-01 又各自独立
 *
 * 之所以任意：它只认周末、不认节假日，合并目标又是「上一个更晚的组」，
 * 撞上长假就成了这副样子。而且条目本身已显示「MM-DD HH:MM」，
 * 头写着 10-11 内容混着 10-10，只会更难分辨。
 *
 * 休市日的归并关系由顶部 ⓘ 归属提示横幅说明（「10-10、10-11 休市，
 * 新闻一并显示」），不必在分组头上再重复一次。
 */
function groupByDate(items: NewsItem[]): Group[] {
  const byDate = new Map<string, NewsItem[]>();
  for (const it of items) {
    const arr = byDate.get(it.date) ?? [];
    arr.push(it);
    byDate.set(it.date, arr);
  }
  // 组间按日期倒序，组内按发布时间倒序
  return [...byDate.entries()]
    .sort((a, b) => (a[0] < b[0] ? 1 : -1))
    .map(([date, list]) => ({
      date,
      items: [...list].sort((a, b) => b.publishedAt.localeCompare(a.publishedAt)),
    }));
}

/** scope + 锚点日期 → 查询区间。日 scope 取单日（走服务端的休市归属）。 */
function scopeRange(period: string, date: string): { from: string; to: string } | null {
  switch (period) {
    case 'D': case 'MIN1': case 'MIN5':
      return { from: date, to: date };
    case 'W': {
      const k = isoWeekKey(date);
      const a = isoWeekMonday(k);
      const b = isoWeekSunday(k);
      return a && b ? { from: a, to: b } : null;
    }
    case 'M': {
      const k = monthKey(date);
      const [y, m] = k.split('-').map(Number);
      if (!y || !m) return null;
      const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
      return { from: `${k}-01`, to: `${k}-${String(last).padStart(2, '0')}` };
    }
    case 'Y': {
      const y = yearKey(date);
      return { from: `${y}-01-01`, to: `${y}-12-31` };
    }
    default:
      return null;
  }
}

